/**
 * PCR Card GSAP Animation Utilities
 * Professional animations using GSAP 3
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { TextPlugin } from 'gsap/TextPlugin';
import { CustomEase } from 'gsap/CustomEase';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, TextPlugin, CustomEase);

// Custom easings
CustomEase.create("smoothOut", "0.25, 0.46, 0.45, 0.94");
CustomEase.create("smoothInOut", "0.76, 0, 0.24, 1");
CustomEase.create("elasticOut", "0.68, -0.55, 0.265, 1.55");

class GSAPAnimations {
    constructor() {
        this.initScrollAnimations();
        this.initHoverAnimations();
        this.initPageTransitions();
        this.initMobileMenu();
        this.initCardAnimations();
        this.initFormAnimations();
        this.initCounterAnimations();
        this.initParallax();
    }

    /**
     * Scroll-triggered animations
     */
    initScrollAnimations() {
        // Fade up elements
        gsap.utils.toArray('.gsap-fade-up').forEach(element => {
            gsap.fromTo(element, 
                {
                    y: 60,
                    opacity: 0
                },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.8,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: element,
                        start: "top 85%",
                        end: "bottom 20%",
                        toggleActions: "play none none reverse"
                    }
                }
            );
        });

        // Fade in from sides
        gsap.utils.toArray('.gsap-fade-left').forEach(element => {
            gsap.fromTo(element,
                {
                    x: -60,
                    opacity: 0
                },
                {
                    x: 0,
                    opacity: 1,
                    duration: 0.8,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: element,
                        start: "top 80%"
                    }
                }
            );
        });

        gsap.utils.toArray('.gsap-fade-right').forEach(element => {
            gsap.fromTo(element,
                {
                    x: 60,
                    opacity: 0
                },
                {
                    x: 0,
                    opacity: 1,
                    duration: 0.8,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: element,
                        start: "top 80%"
                    }
                }
            );
        });

        // Scale animations
        gsap.utils.toArray('.gsap-scale-in').forEach(element => {
            gsap.fromTo(element,
                {
                    scale: 0.8,
                    opacity: 0
                },
                {
                    scale: 1,
                    opacity: 1,
                    duration: 0.6,
                    ease: "back.out(1.7)",
                    scrollTrigger: {
                        trigger: element,
                        start: "top 80%"
                    }
                }
            );
        });

        // Stagger children animations
        gsap.utils.toArray('.gsap-stagger').forEach(container => {
            const children = container.children;
            gsap.fromTo(children,
                {
                    y: 40,
                    opacity: 0
                },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.6,
                    stagger: 0.1,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: container,
                        start: "top 80%"
                    }
                }
            );
        });
    }

    /**
     * Smooth hover animations
     */
    initHoverAnimations() {
        // Buttons
        document.querySelectorAll('.btn, .cta-button').forEach(button => {
            const tl = gsap.timeline({ paused: true });
            
            tl.to(button, {
                y: -2,
                boxShadow: "0 10px 20px rgba(0,0,0,0.15)",
                duration: 0.3,
                ease: "power2.out"
            });

            button.addEventListener('mouseenter', () => tl.play());
            button.addEventListener('mouseleave', () => tl.reverse());
            
            // Click animation
            button.addEventListener('click', function(e) {
                gsap.to(this, {
                    scale: 0.95,
                    duration: 0.1,
                    yoyo: true,
                    repeat: 1,
                    ease: "power2.inOut"
                });
            });
        });

        // Navigation links
        document.querySelectorAll('.nav-link, .modern-nav a').forEach(link => {
            const underline = document.createElement('span');
            underline.style.cssText = `
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                height: 2px;
                background: currentColor;
                transform-origin: left;
            `;
            link.style.position = 'relative';
            link.appendChild(underline);
            
            gsap.set(underline, { scaleX: 0 });
            
            link.addEventListener('mouseenter', () => {
                gsap.to(underline, {
                    scaleX: 1,
                    duration: 0.3,
                    ease: "power2.out"
                });
            });
            
            link.addEventListener('mouseleave', () => {
                gsap.to(underline, {
                    scaleX: 0,
                    duration: 0.3,
                    ease: "power2.out"
                });
            });
        });
    }

    /**
     * Page transition animations
     */
    initPageTransitions() {
        // Animate page load
        gsap.from('.hero-carousel-container', {
            opacity: 0,
            y: 30,
            duration: 1,
            ease: "power3.out",
            delay: 0.2
        });

        // Animate header
        gsap.from('.modern-header', {
            y: -100,
            opacity: 0,
            duration: 0.8,
            ease: "power3.out",
            delay: 0.1
        });

        // Animate sections on load
        gsap.from('.section', {
            opacity: 0,
            y: 50,
            duration: 1,
            stagger: 0.2,
            ease: "power3.out",
            delay: 0.3
        });
    }

    /**
     * Mobile menu animations
     */
    initMobileMenu() {
        const menuToggle = document.querySelector('.mobile-menu-toggle');
        const nav = document.querySelector('.modern-nav');
        
        if (!menuToggle || !nav) return;

        const menuTimeline = gsap.timeline({ paused: true });
        
        // Menu animation
        menuTimeline
            .to(nav, {
                x: 0,
                duration: 0.4,
                ease: "power3.inOut"
            })
            .from(nav.querySelectorAll('.nav-link'), {
                x: 50,
                opacity: 0,
                duration: 0.3,
                stagger: 0.05,
                ease: "power2.out"
            }, "-=0.2");

        // Set initial state
        gsap.set(nav, { x: '100%' });

        let menuOpen = false;
        
        menuToggle.addEventListener('click', () => {
            menuOpen = !menuOpen;
            
            // Animate hamburger to X
            if (menuOpen) {
                gsap.to(menuToggle.querySelector('span:nth-child(1)'), {
                    rotation: 45,
                    y: 7,
                    duration: 0.3,
                    ease: "power2.inOut"
                });
                gsap.to(menuToggle.querySelector('span:nth-child(2)'), {
                    opacity: 0,
                    duration: 0.2
                });
                gsap.to(menuToggle.querySelector('span:nth-child(3)'), {
                    rotation: -45,
                    y: -7,
                    duration: 0.3,
                    ease: "power2.inOut"
                });
                
                menuTimeline.play();
            } else {
                gsap.to(menuToggle.querySelectorAll('span'), {
                    rotation: 0,
                    y: 0,
                    opacity: 1,
                    duration: 0.3,
                    ease: "power2.inOut"
                });
                
                menuTimeline.reverse();
            }
        });
    }

    /**
     * Card animations
     */
    initCardAnimations() {
        document.querySelectorAll('.feature-card, .pricing-card, .stat-card').forEach(card => {
            const tl = gsap.timeline({ paused: true });
            
            tl.to(card, {
                y: -5,
                boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                duration: 0.3,
                ease: "power2.out"
            });

            // Animate icon if present
            const icon = card.querySelector('.feature-icon, .stat-icon');
            if (icon) {
                tl.to(icon, {
                    rotation: 5,
                    scale: 1.1,
                    duration: 0.3,
                    ease: "back.out(2)"
                }, 0);
            }

            card.addEventListener('mouseenter', () => tl.play());
            card.addEventListener('mouseleave', () => tl.reverse());
        });

        // Pokemon card 3D rotation
        const pokemonCard = document.querySelector('.pokemon-card');
        if (pokemonCard) {
            pokemonCard.addEventListener('mousemove', (e) => {
                const rect = pokemonCard.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                
                const centerX = rect.width / 2;
                const centerY = rect.height / 2;
                
                const rotateX = (y - centerY) / 10;
                const rotateY = (centerX - x) / 10;
                
                gsap.to(pokemonCard, {
                    rotationX: rotateX,
                    rotationY: rotateY,
                    duration: 0.5,
                    ease: "power2.out",
                    transformPerspective: 1000
                });
            });
            
            pokemonCard.addEventListener('mouseleave', () => {
                gsap.to(pokemonCard, {
                    rotationX: 0,
                    rotationY: 0,
                    duration: 0.5,
                    ease: "power2.out"
                });
            });
        }
    }

    /**
     * Form animations
     */
    initFormAnimations() {
        // Floating labels
        document.querySelectorAll('.form-control-modern').forEach(input => {
            const label = input.parentElement.querySelector('.form-label-floating');
            if (!label) return;

            input.addEventListener('focus', () => {
                gsap.to(label, {
                    y: -25,
                    scale: 0.85,
                    color: 'var(--primary)',
                    duration: 0.3,
                    ease: "power2.out"
                });
            });

            input.addEventListener('blur', () => {
                if (!input.value) {
                    gsap.to(label, {
                        y: 0,
                        scale: 1,
                        color: 'var(--text-muted)',
                        duration: 0.3,
                        ease: "power2.out"
                    });
                }
            });
        });

        // Submit button animation
        document.querySelectorAll('form').forEach(form => {
            form.addEventListener('submit', function(e) {
                const submitBtn = this.querySelector('[type="submit"]');
                if (submitBtn) {
                    gsap.to(submitBtn, {
                        scale: 0.95,
                        duration: 0.1,
                        yoyo: true,
                        repeat: 1,
                        ease: "power2.inOut"
                    });
                }
            });
        });
    }

    /**
     * Counter animations
     */
    initCounterAnimations() {
        document.querySelectorAll('.stat-number').forEach(counter => {
            const target = parseInt(counter.dataset.target) || 0;
            
            ScrollTrigger.create({
                trigger: counter,
                start: "top 80%",
                once: true,
                onEnter: () => {
                    gsap.from(counter, {
                        textContent: 0,
                        duration: 2,
                        ease: "power3.out",
                        snap: { textContent: 1 },
                        onUpdate: function() {
                            counter.textContent = Math.ceil(counter.textContent);
                        }
                    });
                }
            });
        });
    }

    /**
     * Parallax scrolling effects
     */
    initParallax() {
        // Hero parallax
        gsap.utils.toArray('.hero-carousel-container').forEach(hero => {
            gsap.to(hero, {
                yPercent: -20,
                ease: "none",
                scrollTrigger: {
                    trigger: hero,
                    start: "top top",
                    end: "bottom top",
                    scrub: true
                }
            });
        });

        // Section parallax
        gsap.utils.toArray('.gsap-parallax').forEach(element => {
            const speed = element.dataset.speed || 0.5;
            
            gsap.to(element, {
                yPercent: -50 * speed,
                ease: "none",
                scrollTrigger: {
                    trigger: element,
                    start: "top bottom",
                    end: "bottom top",
                    scrub: true
                }
            });
        });
    }

    /**
     * Utility functions
     */
    static fadeIn(element, duration = 0.5, delay = 0) {
        return gsap.fromTo(element, 
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration, delay, ease: "power2.out" }
        );
    }

    static fadeOut(element, duration = 0.5) {
        return gsap.to(element, { 
            opacity: 0, 
            y: -20, 
            duration, 
            ease: "power2.in",
            onComplete: () => gsap.set(element, { display: 'none' })
        });
    }

    static slideIn(element, direction = 'left', duration = 0.5) {
        const from = direction === 'left' ? { x: -100 } : { x: 100 };
        return gsap.fromTo(element,
            { ...from, opacity: 0 },
            { x: 0, opacity: 1, duration, ease: "power3.out" }
        );
    }

    static bounce(element) {
        return gsap.to(element, {
            y: -10,
            duration: 0.2,
            yoyo: true,
            repeat: 1,
            ease: "power2.inOut"
        });
    }
}

// Initialize animations when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    new GSAPAnimations();
});

// Export for use in other modules
export default GSAPAnimations;